import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { guideRatingUpdater } from './utils/guideRatingUpdater';
import { USE_BACKEND_API } from './config/apiConfig';
import './utils/testImageStorage';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { TourOrderProvider } from './contexts/TourOrderContext';
import { CategoryOrderProvider } from './contexts/CategoryOrderContext';
import { SafariBuilderProvider } from './contexts/SafariBuilderContext';
import { AuthProvider } from './contexts/AuthContext';
import { PricingProvider } from './contexts/PricingContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { AssistantProvider } from './contexts/AssistantContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { GiftCartProvider } from './contexts/GiftCartContext';
import Layout from './components/Layout/Layout';
import ToastManager from './components/Common/ToastManager';
import { RateLimitToast } from './components/ui/RateLimitToast';
import AppSplashScreen from './components/Common/AppSplashScreen';
import InterestPickerScreen from './components/Onboarding/InterestPickerScreen';
import { UserInterestsProvider } from './contexts/UserInterestsContext';
import { LoadingProvider } from './contexts/LoadingContext';
import RouteRestoration from './components/Common/RouteRestoration';
import Home from './pages/Home';
import Tours from './pages/Tours';
import TourDetails from './pages/TourDetails';
import SafariTours from './pages/SafariTours';
import CulturalExperiences from './pages/CulturalExperiences';
import AdventureTours from './pages/AdventureTours';
import PhotographyTours from './pages/PhotographyTours';
import CategoryTours from './pages/CategoryTours';
import CategoryPackages from './pages/CategoryPackages';
import AllCategories from './pages/AllCategories';
import PackageDetails from './pages/PackageDetails';
import About from './pages/About';
import Contact from './pages/Contact';
import DataDebug from './pages/DataDebug';
import Guides from './pages/Guides';
import Careers from './pages/Careers';
import Press from './pages/Press';
import Help from './pages/Help';
import CustomerPortal from './pages/CustomerPortal';
import AccommodationManagement from './components/CustomSafari/AccommodationManagement';
import GroupPortal from './pages/GroupPortal';
import TravelTips from './pages/TravelTips';
import FAQ from './pages/FAQ';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cancellation from './pages/Cancellation';
import Insurance from './pages/Insurance';
import BookingGuide from './pages/BookingGuide';
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminAccess from './pages/AdminAccess';
import Groups from './pages/Admin/Groups';
import AdminGroupPortal from './pages/Admin/AdminGroupPortal';
import GiftsAdmin from './pages/Admin/GiftsAdmin';
import { AdminPortalLogin } from './components/Admin/AdminPortalLogin';
import ClientLogin from './pages/ClientLogin';
import UserProfile from './pages/UserProfile';
import SavedSafaris from './pages/SavedSafaris';
import Wishlist from './pages/Wishlist';
import TieredPackages from './pages/TieredPackages';
import TieredPackageDetails from './pages/TieredPackageDetails';
import MyBookings from './components/Booking/MyBookings';
import GiftShop from './pages/GiftShop/GiftShop';
import { isGiftShopDomain } from './utils/domainUtils';
import './App.css';

function App() {
  useEffect(() => {
    // Initialize database-only mode and database tables
    const initializeDatabase = async () => {
      try {
        if (USE_BACKEND_API) {
          // One-time cache clear for normalization fix
          const CACHE_VERSION = 'v2';
          if (localStorage.getItem('app_cache_version') !== CACHE_VERSION) {
            localStorage.removeItem('category_images_cache');
            localStorage.removeItem('categories_cache_backend');
            localStorage.removeItem('tours_cache_backend');
            localStorage.setItem('app_cache_version', CACHE_VERSION);
          }

          // Preload data for background sync
          try {
            const { tourService } = await import('./services/tourService');
            const { categoryService } = await import('./services/categoryService');
            
            tourService.getAllTours(false).catch(() => { });
            categoryService.getAllCategories(false).then(async (categories: any[]) => {
              // Preload first 3 category images for instant hero slideshow
              const { imageCache } = await import('./utils/imageCache');
              const allTours = await tourService.getAllTours();
              const heroUrls = categories.slice(0, 3).map((cat: any) => {
                const tour = allTours.find(t => t.categoryId === cat.id || t.category === cat.name);
                return tour?.images?.[0];
              }).filter(Boolean) as string[];
              imageCache.preloadImages(heroUrls).catch(() => {});
            }).catch(() => { });
          } catch (e) { }
        } else {
          console.warn('⚠️ Supabase mode is disabled. To use backend API, ensure REACT_APP_USE_BACKEND_API=true in .env');
        }
      } catch (error) {
        console.error('❌ Database initialization error:', error);
      }
    };

    initializeDatabase();
  }, []);

  return (
    <LoadingProvider>
    <ToastProvider>
      <GiftCartProvider>
        <AuthProvider>
          <TourOrderProvider>
            <CategoryOrderProvider>
              <PricingProvider>
                <AssistantProvider>
                  <SafariBuilderProvider>
                    <NavigationProvider>
                      <UserInterestsProvider>
                        <Router>
                        <OnboardingProvider>
                          <RouteRestoration />

                          <AppSplashScreen>
                            <InterestPickerScreen>
                              {isGiftShopDomain() ? (
                                <Routes>
                                  <Route path="/*" element={
                                    <Layout>
                                      <GiftShop />
                                    </Layout>
                                  } />
                                </Routes>
                              ) : (
                                <Routes>
                                  {/* Admin Routes */}
                                  <Route path="/admin/login" element={<AdminLogin />} />
                                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                                  <Route path="/admin/portal-login" element={<AdminPortalLogin />} />
                                  <Route path="/admin/groups" element={<Groups />} />
                                  <Route path="/admin/group-portal/:groupId" element={<AdminGroupPortal />} />
                                  <Route path="/admin/gifts" element={<GiftsAdmin />} />

                                  {/* Client Login Routes */}
                                  <Route path="/client-login" element={<ClientLogin />} />
                                  <Route path="/login" element={<ClientLogin />} />

                                  {/* Public Routes with Layout */}
                                  <Route path="/*" element={
                                    <Layout>
                                      <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/tours" element={<Tours />} />
                                        <Route path="/tours/:id" element={<TourDetails />} />
                                        <Route path="/categories" element={<AllCategories />} />
                                        <Route path="/safari-tours" element={<SafariTours />} />
                                        <Route path="/cultural-experiences" element={<CulturalExperiences />} />
                                        <Route path="/adventure-tours" element={<AdventureTours />} />
                                        <Route path="/photography-tours" element={<PhotographyTours />} />
                                        <Route path="/category/:categoryId" element={<CategoryPackages />} />
                                        <Route path="/category-name/:categoryName" element={<CategoryTours />} />
                                        <Route path="/package/:slug" element={<PackageDetails />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/debug" element={<DataDebug />} />
                                        <Route path="/guides" element={<Guides />} />
                                        <Route path="/careers" element={<Careers />} />
                                        <Route path="/press" element={<Press />} />
                                        <Route path="/help" element={<Help />} />
                                        <Route path="/customer-portal" element={<CustomerPortal />} />
                                        <Route path="/my-accommodations" element={<AccommodationManagement />} />
                                        <Route path="/group-portal/:groupId" element={<GroupPortal />} />
                                        <Route path="/booking-guide" element={<BookingGuide />} />
                                        <Route path="/travel-tips" element={<TravelTips />} />
                                        <Route path="/faq" element={<FAQ />} />
                                        <Route path="/privacy" element={<Privacy />} />
                                        <Route path="/terms" element={<Terms />} />
                                        <Route path="/cancellation" element={<Cancellation />} />
                                        <Route path="/insurance" element={<Insurance />} />
                                        <Route path="/admin-access" element={<AdminAccess />} />
                                        <Route path="/profile" element={<UserProfile />} />
                                        <Route path="/my-safaris" element={<SavedSafaris />} />
                                        <Route path="/wishlist" element={<Wishlist />} />
                                        <Route path="/my-bookings" element={<MyBookings />} />
                                        <Route path="/packages" element={<TieredPackages />} />
                                        <Route path="/tiered-packages/:id" element={<TieredPackageDetails />} />
                                        <Route path="/gift-shop" element={<GiftShop />} />
                                      </Routes>
                                    </Layout>
                                  } />
                                </Routes>
                              )}
                            </InterestPickerScreen>
                          </AppSplashScreen>
                          <ToastManager />
                          <RateLimitToast />
                        </OnboardingProvider>
                        </Router>
                      </UserInterestsProvider>
                    </NavigationProvider>
                  </SafariBuilderProvider>
                </AssistantProvider>
              </PricingProvider>
            </CategoryOrderProvider>
          </TourOrderProvider>
        </AuthProvider>
      </GiftCartProvider>
    </ToastProvider>
    </LoadingProvider>
  );
}

export default App;
